import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

const DEFAULT_THRESHOLD_PX = 80;

export interface UseNearBottomScrollOptions {
  /** Dependency that changes when content grows (messages, transcript, etc.). */
  contentKey: unknown;
  thresholdPx?: number;
}

export interface UseNearBottomScrollResult {
  showJumpToLatest: boolean;
  jumpToLatest: () => void;
  onScroll: () => void;
}

/**
 * Auto-scrolls only when the user is already near the bottom.
 * Otherwise exposes a "Jump to latest" affordance.
 */
export function useNearBottomScroll(
  scrollRef: RefObject<HTMLElement | null>,
  { contentKey, thresholdPx = DEFAULT_THRESHOLD_PX }: UseNearBottomScrollOptions
): UseNearBottomScrollResult {
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const nearBottomRef = useRef(true);

  const measureNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distance <= thresholdPx;
  }, [scrollRef, thresholdPx]);

  const onScroll = useCallback(() => {
    const near = measureNearBottom();
    nearBottomRef.current = near;
    setShowJumpToLatest(!near);
  }, [measureNearBottom]);

  const jumpToLatest = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    nearBottomRef.current = true;
    setShowJumpToLatest(false);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (nearBottomRef.current || measureNearBottom()) {
      el.scrollTop = el.scrollHeight;
      nearBottomRef.current = true;
      setShowJumpToLatest(false);
    } else {
      setShowJumpToLatest(true);
    }
  }, [contentKey, measureNearBottom, scrollRef]);

  return { showJumpToLatest, jumpToLatest, onScroll };
}
